import { type ReactNode, useMemo, useState } from 'react';

import {
  type ColorToken,
  CountPill,
  DonutChart,
  type DonutSegment,
  LabeledFilterSelect,
  LegendRow,
  StackedBar,
  type StackedBarSegment,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import {
  type LifeReadinessResponse,
  type ReadinessCategory,
  type ReadinessFacet,
} from '@backrs_ui/grow-ui/src/redux/coachingAnalytics';

import { Box, type SxProps, type Theme, Typography } from '@mui/material';

import {
  bucketCount,
  completionStatusLabel,
  humanize,
  percentOf,
  percentShares,
  readinessBandLabel,
} from './analytics.labels';
import { pieLegendSx, pieRowSx } from './analytics.styles';
import { AnalyticsCard } from './AnalyticsCard';
import { SubsectionHeader } from './AnalyticsSubsectionHeader';
import { useAgentContext } from './LifeReadinessCards.agentContext';
import { type StudentDataHandle, useStudentData } from './useStudentData';
import {
  COLUMNS,
  COMMON_COLUMNS,
  lifeReadinessAnsweredColumn,
  readinessBandColumns,
  readinessFacetColumns,
} from './viewData.columns';

const { lifeReadiness, hover, viewData } = appStrings.modules.coachingAnalytics;

const COMPLETION_COLUMNS = [
  ...COMMON_COLUMNS,
  COLUMNS.lifeReadinessStatus,
  COLUMNS.lifeReadinessPercent,
];

const RUNG_SEGMENTS: readonly {
  key: string;
  label: string;
  color: ColorToken;
  labelColor: ColorToken;
}[] = (
  [
    { key: 'not_yet', color: 'Gray/200', labelColor: 'Colors/Text/text-primary' },
    { key: 'early', color: 'BlueDark/300', labelColor: 'Colors/Base/White' },
    { key: 'strong', color: 'BlueDark/500', labelColor: 'Colors/Base/White' },
    { key: 'advanced', color: 'Gray/800', labelColor: 'Colors/Base/White' },
  ] as const
).map((rung) => ({ ...rung, label: readinessBandLabel(rung.key) }));

const CATEGORY_BAR_HEIGHT = 24;

const COMPLETION_STATUSES: readonly { id: string; color: ColorToken }[] = [
  { id: 'completed', color: 'Blue/800' },
  { id: 'in_progress', color: 'Blue/500' },
  { id: 'not_started', color: 'Blue/100' },
];

const PIE_SIZE = 150;

const CompletionCard = ({ data }: Readonly<{ data: LifeReadinessResponse }>) => {
  const { completion } = data;
  const shares = percentShares(
    COMPLETION_STATUSES.map(
      (status) => completion.statuses.find((row) => row.id === status.id)?.count ?? 0,
    ),
    completion.total,
  );
  const rows = COMPLETION_STATUSES.map((status, index) => ({
    ...status,
    label: completionStatusLabel(status.id),
    pct: shares[index],
  }));
  const segments: DonutSegment[] = rows.map((row) => ({
    label: row.label,
    value: row.pct,
    color: row.color,
  }));

  const students = useStudentData(
    'coaching-analytics-card-life-readiness-completion',
    lifeReadiness.completion.title,
    COMPLETION_COLUMNS,
  );
  const selectStatus = (index: number) =>
    students.openSegment({
      label: rows[index].label,
      where: [{ field: 'life_readiness_status', op: 'eq', value: rows[index].id }],
    });

  return (
    <AnalyticsCard
      title={lifeReadiness.completion.title}
      subtitle={lifeReadiness.completion.subtitle(data.students.total)}
      info={[lifeReadiness.completion.caveat(completion.questions.visible)]}
      viewData={students}
    >
      <Box sx={pieRowSx}>
        <DonutChart
          id="coaching-analytics-completion-donut"
          variant={{ type: 'multi', segments }}
          size={PIE_SIZE}
          cutout={0}
          onSegmentClick={selectStatus}
        />
        <Box sx={pieLegendSx}>
          {rows.map((row, index) => (
            <LegendRow
              key={row.id}
              id={`coaching-analytics-life-readiness-legend-${row.id}`}
              color={row.color}
              label={row.label}
              pct={row.pct}
              onClick={() => selectStatus(index)}
            />
          ))}
        </Box>
      </Box>
    </AnalyticsCard>
  );
};

const categoryName = (row: ReadinessCategory): string => row.name ?? humanize(String(row.id));

const ReadinessCategoryBar = ({
  row,
  students,
}: Readonly<{ row: ReadinessCategory; students: StudentDataHandle }>) => {
  const segments: StackedBarSegment[] = RUNG_SEGMENTS.map((segment) => {
    const count = bucketCount(row.answers.bands, segment.key);
    const pct = percentOf(count, row.answers.total);

    return {
      key: segment.key,
      id: `coaching-analytics-readiness-${row.id}-${segment.key}`,
      pct,
      color: segment.color,
      labelColor: segment.labelColor,
      tooltip: hover.countOfAnswers(segment.label, count, row.answers.total),
      onClick: () =>
        students.openSegment({
          label: viewData.segmentLabel(categoryName(row), segment.label),
          where: [
            { field: 'life_readiness_bands', key: String(row.id), op: 'eq', value: segment.key },
          ],
        }),
    };
  });

  const coverage = lifeReadiness.byCategory.coverage(
    row.coverage.answered,
    row.coverage.answerable,
  );

  return (
    <Box sx={categoryRowSx}>
      <Box sx={categoryHeaderSx}>
        <Typography variant="text-xs-medium" sx={categoryLabelSx}>
          {categoryName(row)}
        </Typography>
        <Typography variant="text-xs-regular" sx={categoryLabelSx}>
          {coverage}
        </Typography>
      </Box>
      <StackedBar segments={segments} height={CATEGORY_BAR_HEIGHT} />
    </Box>
  );
};

const ByCategoryCard = ({ data }: Readonly<{ data: LifeReadinessResponse }>) => {
  const { readiness_by_category: readiness, completion } = data;

  const columns = useMemo(
    () => [
      ...COMMON_COLUMNS,
      COLUMNS.lifeReadinessStatus,
      COLUMNS.lifeReadinessPercent,
      lifeReadinessAnsweredColumn(completion.questions.visible),
      ...readinessBandColumns(
        readiness.categories.map((row) => ({ key: String(row.id), name: categoryName(row) })),
      ),
    ],
    [completion.questions.visible, readiness.categories],
  );
  const students = useStudentData(
    'coaching-analytics-card-readiness-by-category',
    lifeReadiness.byCategory.title,
    columns,
    lifeReadiness.byCategory.viewDataNote,
  );

  return (
    <AnalyticsCard
      title={lifeReadiness.byCategory.title}
      subtitle={lifeReadiness.byCategory.subtitle(readiness.students_started)}
      info={[lifeReadiness.byCategory.caveat]}
      viewData={students}
    >
      <Box sx={legendSx}>
        {RUNG_SEGMENTS.map((segment) => (
          <LegendRow key={segment.key} color={segment.color} label={segment.label} />
        ))}
      </Box>
      <Box sx={categoryListSx}>
        {readiness.categories.map((row) => (
          <ReadinessCategoryBar key={row.id} row={row} students={students} />
        ))}
      </Box>
    </AnalyticsCard>
  );
};

const FacetBar = ({
  facet,
  students,
}: Readonly<{ facet: ReadinessFacet; students: StudentDataHandle }>) => {
  const segments: StackedBarSegment[] = RUNG_SEGMENTS.map((segment) => {
    const count = bucketCount(facet.answers.bands, segment.key);

    return {
      key: segment.key,
      id: `coaching-analytics-readiness-facet-${facet.id}-${segment.key}`,
      pct: percentOf(count, facet.answers.total),
      color: segment.color,
      labelColor: segment.labelColor,
      tooltip: hover.countThenLabel(count, segment.label),
      onClick: () =>
        students.openSegment({
          label: viewData.segmentLabel(facet.name, segment.label),
          where: [
            { field: 'life_readiness_facets', key: String(facet.id), op: 'eq', value: segment.key },
          ],
        }),
    };
  });

  return (
    <Box sx={facetRowSx}>
      <Typography variant="text-sm-medium" sx={facetLabelSx}>
        {facet.name}
      </Typography>
      <Box sx={facetBarLineSx}>
        <Box sx={facetBarWrapSx}>
          <StackedBar segments={segments} height={CATEGORY_BAR_HEIGHT} />
        </Box>
        <CountPill value={facet.answers.total} sx={facetTotalSx} />
      </Box>
    </Box>
  );
};

/** One category at a time behind a dropdown; one stacked bar per visible facet. */
const ByFacetCard = ({ data }: Readonly<{ data: LifeReadinessResponse }>) => {
  const { categories } = data.readiness_by_facet;
  const [selectedId, setSelectedId] = useState(categories[0]?.id ?? '');
  const category = categories.find((entry) => entry.id === selectedId) ?? categories[0];
  const columns = useMemo(
    () => [
      ...COMMON_COLUMNS,
      ...readinessFacetColumns(
        (category?.facets ?? []).map((facet) => ({ key: String(facet.id), name: facet.name })),
      ),
    ],
    [category],
  );
  const students = useStudentData(
    'coaching-analytics-card-readiness-by-facet',
    category
      ? viewData.segmentLabel(lifeReadiness.byFacet.title, category.name)
      : lifeReadiness.byFacet.title,
    columns,
  );

  if (!category) return null;

  return (
    <AnalyticsCard
      title={lifeReadiness.byFacet.title}
      infoTitle={category.name}
      info={[...(category.description ? [category.description] : []), lifeReadiness.byFacet.caveat]}
      viewData={students}
      belowHeader={
        <Box sx={pickerSx}>
          <LabeledFilterSelect
            id="coaching-analytics-card-readiness-by-facet-category-select"
            label={lifeReadiness.byFacet.categoryLabel}
            hideLabel
            value={category.id}
            options={categories.map((entry) => ({ value: entry.id, label: entry.name }))}
            onChange={setSelectedId}
          />
        </Box>
      }
    >
      <Box sx={legendSx}>
        {RUNG_SEGMENTS.map((segment) => (
          <LegendRow key={segment.key} color={segment.color} label={segment.label} />
        ))}
      </Box>
      <Box sx={categoryListSx}>
        {category.facets.map((facet) => (
          <FacetBar key={facet.id} facet={facet} students={students} />
        ))}
      </Box>
    </AnalyticsCard>
  );
};

export const LifeReadinessSection = ({
  data,
  action,
  detail = false,
}: Readonly<{ data: LifeReadinessResponse; action?: ReactNode; detail?: boolean }>) => {
  useAgentContext(data, detail);

  return (
    <Box sx={sectionSx}>
      <SubsectionHeader
        toolId="life_readiness"
        title={lifeReadiness.title}
        subtitle={lifeReadiness.subtitle}
        action={
          action ?? (
            <LubavButton
              id="coaching-analytics-life-readiness-discuss-lubav"
              label={appStrings.modules.coachingAnalytics.successSystem.discussWithLubav}
              promptButtonId="coaching_analytics_life_readiness"
            />
          )
        }
      />
      <Box sx={gridSx}>
        <CompletionCard data={data} />
        <ByCategoryCard data={data} />
      </Box>
      {detail && <ByFacetCard data={data} />}
    </Box>
  );
};

const sectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  width: '100%',
});

const gridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-3xl'],
  alignItems: 'stretch',
  '@container (min-width: 900px)': {
    gridTemplateColumns: 'minmax(0, 0.75fr) minmax(0, 1.25fr)',
  },
});

const legendSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: backrs.spacing['spacing-xl'],
  width: '100%',
});

const categoryListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});

const categoryRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  width: '100%',
});

const categoryHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

const categoryLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const pickerSx: SxProps<Theme> = {
  width: '100%',
  maxWidth: 360,
  alignSelf: 'flex-start',
};

const facetRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  '@container (min-width: 650px)': {
    flexDirection: 'row',
    alignItems: 'center',
    gap: backrs.spacing['spacing-lg'],
  },
});

const facetLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  '@container (min-width: 650px)': {
    width: 200,
    flexShrink: 0,
  },
});

const facetBarLineSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  flex: 1,
  minWidth: 0,
});

const facetBarWrapSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
};

const facetTotalSx: SxProps<Theme> = {
  minWidth: 40,
  flexShrink: 0,
};
